import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import InputField from "../../../components/inputFields/InputField";
import Button from "../../../components/buttons/Button";
import FormikDropdown from "../../../components/inputFields/FormikDropDown";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { patchHudPatientsHistory, postMedicineChanges } from "../../../services/hud/hud_patients";
import { toast } from "react-toastify";
import FormikMultiSelectDropdown from "../../../components/inputFields/FormikMultiSelectDropdown";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import { hoThyroidOptions } from "../../../constants/h_o_thyroid_options";
import { formatDateTime } from "../../../utils/FormatDateTime";
import ShowModal from "../../../components/Hud/ShowModal";
import NewPatientMedicineView from "./new_patient_medicine_view";
import { getNewHudOintments, getNewHudPatientsHistory, getNewHudTablets, patchNewHudPatientsHistory, postNewHudMedicineChanges, postNewHudPatientsPackageApprovals } from "../../../services/new_clinic/new_hud_patients";

const NewPatientHistory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
    const [medicineModalOpenFor, setIsMedicineModalOpenFor] = useState("");

    const [initialValues, setInitialValues] = useState({
        "patient_history_id": "",
        "duration": "",
        "thyroid": "",
        "auto_immunediseases": "",
        "ho_jaundice": "",
        "fh_auto_immunediseases": "",
        "ophthalmological_problems": "",
        "ho_hospitalisathion": "",
        "psychiatric_history": "",
        "keloidol_tendencey": "",
        "epilepsy": "",
        "blood_group": "",
        "history_of_steroid": "",
        "photo_of_date": "",
        "antibodies_to_thyroid": "",
        "ho_epilepsy": "",
        "activity": "",
        "fh_vitiligo": "",
        "ho_thyroid": "",
        "hb": "",
        "rating": "",
        "tablets": [],
        "ointments": [],
    });

    const [availableTablets, setAvailableTablets] = useState([]);
    const [availableOintments, setAvailableOintments] = useState([]);
    const [rowData, setRowData] = useState({});
    const [loading, setLoading] = useState(false);

    const openMedicineModal = (modelFor) => {
        setIsMedicineModalOpen(true);
        setIsMedicineModalOpenFor(modelFor);
    };

    // Close modal
    const closeMedicineModal = () => {
        setIsMedicineModalOpen(false);
        setIsMedicineModalOpenFor("");
    };

    useEffect(() => {
        const patientData = location.state;
        if (patientData) {
            setRowData(patientData);
            fetchAvailableTablets();
            fetchAvailableOintments();
            fetchData(patientData.id);
        } else {
            toast.error("Error while looking for patient");
            navigate("/new-hud-patients");
        }
    }, [location.state]);

    const fetchData = async (patientId) => {
        setLoading(true);
        try {
            let res = await getNewHudPatientsHistory(patientId);
            if (res.status === 200) {
                setInitialValues(res.data);
            } else {
                toast.error("Unable to fetch patient details");
            }
        } catch (error) {
            console.error("Unable to fetch patient details:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTablets = async () => {
        try {
            let res = await getNewHudTablets();
            if (res.status === 200) {
                setAvailableTablets(res.data);
            } else {
                toast.error("Unable to fetch Tablets");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const fetchAvailableOintments = async () => {
        try {
            let res = await getNewHudOintments();
            if (res.status === 200) {
                setAvailableOintments(res.data);
            } else {
                toast.error("Unable to fetch Tablets");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        handleMedicinesUpdate(values);
        try {
            let updatedValues = {
                ...values,
                fh_vitiligo: `${values.fh_vitiligo}`,
                activity: `${values.activity}`,
                antibodies_thyroid: `${values.antibodies_to_thyroid}`,
            };
            const response = await patchNewHudPatientsHistory(rowData.id, updatedValues);
            if (response?.status === 200) {
                toast.success("History updated successfully!");
                navigate(-1);
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMedicinesUpdate = async (values) => {
        try {
            const response = await postNewHudMedicineChanges({
                medicines: [...values.tablets, ...values.ointments],
            }, rowData.id);

            if (response?.status === 200) {
                toast.success("Medicines updated successfully!");
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    const handlePhotoOfDateUpdate = async (values) => {
        try {
            const response = await postNewHudPatientsPackageApprovals({
                patient: rowData.id,
                created_at: new Date(new Date(values.photo_of_date).getTime() + (5 * 60 * 60 * 1000) + (30 * 60 * 1000))
            });

            if (response?.status === 201) {
                toast.success("Photo of date updated successfully!");
                fetchData(rowData.id);
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    return (
        loading ? <CustomLoading /> :
            <div>
                <div className="flex flex-row justify-between mb-2">
                    <div className="flex flex-row gap-2">
                        <StyledHeadingText size={"text-xs"} text={"Patient Details: "} />
                        <StyledHeadingText size={"text-xs"} TextColor="text-red-600" text={`${rowData.name} (${"Age: " + rowData.age || "DOB: " + rowData.dob} | Gen: ${rowData.gender})`} />
                        <StyledHeadingText size={"text-xs"} text={" | "} />
                        <StyledHeadingText size={"text-xs"} text={"Registration Date: "} />
                        <StyledHeadingText size={"text-xs"} TextColor="text-red-600" text={formatDateTime(rowData.date_created)} />
                    </div>
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/package-invoice' },
                        { name: 'Patients', link: '/new-hud-patients' },
                        { name: 'Patient History' },
                    ]} />
                </div>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form className="space-y-4">
                            <div className="grid gap-1 md:grid-cols-3 lg:grid-cols-4">
                                <InputField
                                    label="Duration"
                                    name="duration"
                                    type="text"
                                    placeholder="XX"
                                />
                                <FormikDropdown
                                    label="Thyroid"
                                    name="thyroid"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Auto Immune Disease"
                                    name="auto_immunediseases"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Jaundice"
                                    name="ho_jaundice"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="F/H (A) Auto Imm. Dis."
                                    name="fh_auto_immunediseases"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Ophthalmological Problems"
                                    name="ophthalmological_problems"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Hospitalization"
                                    name="ho_hospitalisathion"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Psychiatric History"
                                    name="psychiatric_history"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Keloidol Tendency"
                                    name="keloidol_tendencey"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Epilepsy"
                                    name="epilepsy"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Blood Group"
                                    name="blood_group"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "A+", label: "A+" },
                                        { value: "A-", label: "A-" },
                                        { value: "B+", label: "B+" },
                                        { value: "B-", label: "B-" },
                                        { value: "O+", label: "O+" },
                                        { value: "O-", label: "O-" },
                                        { value: "AB+", label: "AB+" },
                                        { value: "AB-", label: "AB-" },
                                    ]}
                                />
                                <InputField
                                    label="HB"
                                    name="hb"
                                    type="text"
                                    placeholder="XX"
                                />
                                <FormikDropdown
                                    label="History of steroids"
                                    name="history_of_steroid"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "YES", label: "Yes" },
                                        { value: "NO", label: "No" },
                                    ]}
                                />
                                <div className="flex flex-row items-end gap-2">
                                    <div className="flex-1">
                                        <InputField
                                            label="Photo Date"
                                            name="photo_of_date"
                                            type="date"
                                            placeholder="Date"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <div
                                            className="text-white bg-green-500 hover:bg-primary-dark font-sm text-xs px-8 py-1 text-center max-h-12 rounded cursor-pointer"
                                            onClick={() => handlePhotoOfDateUpdate(values)}
                                        >
                                            Update
                                        </div>
                                        <div
                                            className="text-white bg-primary hover:bg-primary-dark font-sm text-xs px-8 py-1 text-center max-h-12 mb-1 rounded cursor-pointer"
                                            onClick={() => openMedicineModal("photo")}
                                        >
                                            View
                                        </div>
                                    </div>
                                </div>
                                <InputField
                                    label="Antibodies to Thyroid"
                                    name="antibodies_to_thyroid"
                                    placeholder="Antibodies to Thyroid"
                                />
                                <FormikDropdown
                                    label="H/O Epilepsy"
                                    name="ho_epilepsy"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "YES", label: "Yes" },
                                        { value: "NO", label: "No" },
                                    ]}
                                />
                            </div>
                            <div className="grid gap-1 md:grid-cols-3">
                                <FormikMultiSelectDropdown
                                    label="Activity (Koebnerisation)"
                                    name="activity"
                                    needSearch
                                    placeholder="Select Activity (Koebnerisation)"
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikMultiSelectDropdown
                                    label="F/H (B) Vitiligo"
                                    name="fh_vitiligo"
                                    needSearch
                                    placeholder="Select F/H (B) Vitiligo"
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "(Father)", label: "(Father)" },
                                        { value: "(Mother)", label: "(Mother)" },
                                        { value: "(Sister)", label: "(Sister)" },
                                        { value: "(Positive Father)", label: "(Positive father)" },
                                        { value: "(Positive Mother)", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Thyroid"
                                    name="ho_thyroid"
                                    placeholder="Select"
                                    needSearch
                                    options={hoThyroidOptions.map((item) => ({
                                        value: item,
                                        label: item,
                                    }))}
                                />
                            </div>
                            <div className="grid gap-1 grid-cols-1 md:grid-cols-2">
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Tablets"
                                            name="tablets"
                                            needSearch
                                            placeholder="Select Tablets"
                                            options={availableTablets.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-xs px-8 py-1.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("tablet")}
                                    >
                                        View
                                    </div>
                                </div>
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Ointment-Lotion"
                                            name="ointments"
                                            needSearch
                                            placeholder="Select Ointment-Lotion"
                                            options={availableOintments.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-xs px-8 py-1.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("ointment")}
                                    >
                                        View
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="submit"
                                    className="bg-primary text-white px-4 py-2 rounded text-xs"
                                    disabled={isSubmitting}
                                >
                                    Update
                                </Button>
                                <Button
                                    type="button"
                                    className="bg-gray-500 text-white px-4 py-2 rounded text-xs"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
                <ShowModal modalWidth={"50%"} contentLabel={"View Medcine alert"} isOpen={isMedicineModalOpen} onRequestClose={closeMedicineModal} componentToPass={<NewPatientMedicineView openedFor={medicineModalOpenFor} patientId={rowData.id} />} />
            </div >
    );
};

export default NewPatientHistory;
